'use client';

import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { z } from 'zod';

import locationIcon from '@/assets/images/location-icon.svg';
import mailIcon from '@/assets/images/mail-icon.svg';
import phoneIcon from '@/assets/images/phone-icon.svg';
import rightIcon from '@/assets/images/right-arrow-icon.svg';

import '@/styles/globals.v2.css';

import Image from 'next/image';

import Button from '@/components/V2/Button';
import TextInput from '@/components/V2/TextInput';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import cn from '@/helpers/utils/classNames';
import { useSendContactUsEmailMutation } from '@/store/api/ContactUs';
import { TextareaAutosize } from '@mui/material';

// Zod schema with phone validation
const contactFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z
    .string()
    .min(1, 'Email is required') // Check if the email field is empty first
    .refine((value: string) => value === '' || z.string().email().safeParse(value).success, {
      message: 'Invalid email address',
    }),
  phone: z
    .string()
    .optional()
    .refine(value => !value || /^\d{10,11}$/.test(value), 'Phone number must be 10 digits'),
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().min(1, 'Message is required'),
});
const ContactUsForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<any>({});
  const [sendContactUsEmail, { isLoading }] = useSendContactUsEmailMutation();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Update the formData state
    setFormData({
      ...formData,
      [name]: value,
    });

    // Ensure `name` is a valid key of the schema
    if (name in contactFormSchema.shape) {
      const fieldName = name as keyof typeof contactFormSchema.shape; // Explicitly cast `name` to the correct type

      // Create a dynamic schema for the specific field being changed
      const singleFieldSchema = z.object({
        [fieldName]: contactFormSchema.shape[fieldName],
      });

      // Validate only the current field
      const validation = singleFieldSchema.safeParse({ [fieldName]: value });

      if (!validation.success) {
        // If there's an error for the field, update errors state
        setErrors({
          ...errors,
          [name]: validation.error.errors[0].message,
        });
      } else {
        // Clear the error for the field if it passes validation
        setErrors({
          ...errors,
          [name]: '',
        });
      }
    }
  };

  const setFieldErrors = (validationErrors: z.ZodIssue[]) => {
    const fieldErrors: any = {};
    validationErrors.forEach((err: z.ZodIssue) => {
      if (err.path[0]) {
        fieldErrors[err.path[0]] = err.message;
      }
    });
    setErrors(fieldErrors);
  };
  const processServerErrors = (serverResponse: any) => {
    const serverErrors = serverResponse.validationErrors ? JSON.parse(serverResponse.validationErrors) : {};
    if (serverErrors) {
      setErrors(serverErrors);
    }
    toast.error(serverResponse.message || 'Failed to send the message.');
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validation = contactFormSchema.safeParse(formData);
    if (!validation.success) {
      setFieldErrors(validation.error.errors);
    } else {
      setErrors({});
      await submitContactForm();
    }
  };
  const submitContactForm = async () => {
    const payload = {
      fullName: formData.name,
      email: formData.email,
      phone: formData.phone || '',
      subject: formData.subject,
      message: formData.message,
    };
    try {
      const response = await sendContactUsEmail(payload).unwrap();
      if (response.success) {
        toast.success(response.message || 'Message sent successfully!');
        resetForm();
      } else {
        processServerErrors(response);
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'An error occurred. Please try again later.';
      const validationErrors = error?.data?.validationErrors ? JSON.parse(error.data.validationErrors) : {};
      if (validationErrors) {
        setErrors(validationErrors);
      }
      toast.error(errorMessage);
    }
  };
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };
  return (
    <div
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable
      )}
    >
      <div className="v2-grid v2-gap-[64px] v2-pt-[64px]">
        <h2 className="v2-text-[#101828] v2-font-light v2-text-4xl v2-font-sweet-sans-pro-light v2-text-center">
          Contact <span className="v2-font-semibold v2-font-sweet-sans-pro-semi-bold">Customer Support</span>
        </h2>
        <div className="v2-flex lg:v2-flex md:v2-flex sm:v2-block xs:v2-block">
          <div className="v2-px-[46px] lg:v2-px-[46px] md:v2-px-[16px] sm:v2-px-0 xs:v2-px-0 v2-border-r md:v2-border-r sm:v2-border-r-0 xs:v2-border-r-0 md:v2-pb-0 sm:v2-pb-[32px] xs:v2-pb-[32px] md:v2-border-b-0 sm:v2-border-b xs:v2-border-b md:v2-mb-0 sm:v2-mb-[32px] xs:v2-mb-[32px] v2-border-[#EAECF0] v2-flex v2-v2-items-center v2-w-full">
            <div className="v2-mr-[20px] v2-max-w-[77px] lg:v2-max-w-[77px] md:v2-max-w-[44px] sm:v2-max-w-[77px] v2-w-full v2-h-[77px] lg:v2-h-[77px] md:v2-h-[44px] sm:v2-h-[77px] v2-shadow-round v2-rounded-full">
              <Image className="" src={locationIcon} alt="Location Icon" />
            </div>
            <div className="v2-text-left">
              <h5 className="v2-text-[#101828] v2-font-light v2-text-base v2-font-sweet-sans-pro-light v2-mb-[8px]">
                Address
              </h5>
              <p className="v2-text-[#666666] v2-font-normal v2-font-sweet-sans-pro v2-text-lg">
                P.O. Box 92902 Southlake, TX 76092
              </p>
            </div>
          </div>
          <div className="v2-px-[46px] lg:v2-px-[46px] md:v2-px-[16px] sm:v2-px-0 xs:v2-px-0 v2-border-r md:v2-border-r sm:v2-border-r-0 xs:v2-border-r-0 md:v2-pb-0 sm:v2-pb-[32px] xs:v2-pb-[32px] md:v2-border-b-0 sm:v2-border-b xs:v2-border-b md:v2-mb-0 sm:v2-mb-[32px] xs:v2-mb-[32px] v2-border-[#EAECF0] v2-flex v2-v2-items-center v2-w-full">
            <div className="v2-mr-[20px] v2-max-w-[77px] lg:v2-max-w-[77px] md:v2-max-w-[44px] sm:v2-max-w-[77px] v2-w-full v2-h-[77px] lg:v2-h-[77px] md:v2-h-[44px] sm:v2-h-[77px] v2-shadow-round v2-rounded-full">
              <Image className="" src={phoneIcon} alt="Phone Icon" />
            </div>
            <div className="v2-text-left">
              <h5 className="v2-text-[#101828] v2-font-light v2-text-base v2-font-sweet-sans-pro-light v2-mb-[8px]">
                Phone
              </h5>
              <a href="tel:855-940-5234" className="v2-text-[#666666] v2-font-normal v2-font-sweet-sans-pro v2-text-lg">
                855-940-5234
              </a>
            </div>
          </div>
          <div className="v2-px-[46px] lg:v2-px-[46px] md:v2-px-[16px] sm:v2-px-0 xs:v2-px-0 v2-flex v2-v2-items-center v2-w-full v2-pr-0 lg:v2-pr-0 md:v2-pr-0 sm:v2-pr-0 xs:v2-pr-0">
            <div className="v2-mr-[20px] v2-max-w-[77px] lg:v2-max-w-[77px] md:v2-max-w-[44px] sm:v2-max-w-[77px] v2-w-full v2-h-[77px] lg:v2-h-[77px] md:v2-h-[44px] sm:v2-h-[77px] v2-shadow-round v2-rounded-full">
              <Image className="" src={mailIcon} alt="Mail Icon" />
            </div>
            <div className="v2-text-left">
              <h5 className="v2-text-[#101828] v2-font-light v2-text-base v2-font-sweet-sans-pro-light v2-mb-[8px]">
                Email
              </h5>
              <a
                href="mailto:support@pawtree.com"
                className="v2-text-[#666666] v2-font-normal v2-font-sweet-sans-pro v2-text-lg"
              >
                support@pawtree.com
              </a>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="v2-grid v2-grid-cols-1 v2-gap-[32px]">
          <div className="v2-grid v2-grid-cols-2 v2-gap-[32px]">
            <div>
              <label
                htmlFor="name"
                className="v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-mb-[6px] v2-block"
              >
                Name*
              </label>
              <TextInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Name"
                inputClassName="v2-h-[44px] v2-border-[#D0D5DD]"
              />
              {errors.name && (
                <p className="v2-text-red-600 v2-text-base v2-font-normal v2-font-sweet-sans-pro">{errors.name}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="email"
                className="v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-mb-[6px] v2-block"
              >
                Email Address*
              </label>
              <TextInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
                inputClassName="v2-h-[44px] v2-border-[#D0D5DD]"
              />
              {errors.email && (
                <p className="v2-text-red-600 v2-text-base v2-font-normal v2-font-sweet-sans-pro">{errors.email}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="phone"
                className="v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-mb-[6px] v2-block"
              >
                Phone Number (Optional)
              </label>
              <TextInput
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number"
                inputClassName="v2-h-[44px] v2-border-[#D0D5DD]"
                maxLength={10}
              />
              {errors.phone && (
                <p className="v2-text-red-600 v2-text-base v2-font-normal v2-font-sweet-sans-pro">{errors.phone}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="subject"
                className="v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-mb-[6px] v2-block"
              >
                Subject*
              </label>
              <TextInput
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Subject"
                inputClassName="v2-h-[44px] v2-border-[#D0D5DD]"
              />
              {errors.subject && (
                <p className="v2-text-red-600 v2-text-base v2-font-normal v2-font-sweet-sans-pro">{errors.subject}</p>
              )}
            </div>
          </div>
          <div className="">
            <label
              htmlFor="message"
              className="v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-mb-[6px] v2-block"
            >
              Your Message*
            </label>
            <TextareaAutosize
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Type your message.."
              className="v2-w-full v2-border v2-rounded v2-py-[12px] v2-px-[14px] v2-text-base v2-font-sweet-sans-pro v2-text-gray-900 disabled:v2-text-gray v2-font-light placeholder:v2-text-gray-500 hover:v2-border-primary-500 focus-visible:v2-border-primary-600 focus-visible:v2-outline-none focus-visible:v2-shadow-input-focus-primary disabled:v2-bg-gray-50 disabled:v2-border-gray-300 disabled:v2-cursor-not-allowed v2-appearance-remove !v2-h-[154px] v2-resize-none v2-border-[#D0D5DD]"
            />
            {errors.message && (
              <p className="v2-text-red-600 v2-text-base v2-font-normal v2-font-sweet-sans-pro">{errors.message}</p>
            )}
          </div>
          <div className="v2-flex v2-items-center v2-justify-center">
            <Button
              type="submit"
              variant="filled"
              btnSize="lg"
              scheme="primary"
              className="v2-font-sweet-sans-pro-medium v2-flex v2-items-center v2-gap-[6px]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-transparent border-t-black"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                <>
                  Submit
                  <Image className="" src={rightIcon} alt="Submit Icon" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ContactUsForm;
